/* buildAiReviewData.js
 *
 * Builds structured data for AI review popups, including:
 * - linked reviews
 * - reviewer information
 * - assignment recommendations
 * - normalized AI review data
 * - normalized AI presentation data
 * - popup-ready route decision, reasoning, trust, and routing models
 */

import { recommendForSubmission } from "../utils/assignEngine";
import { buildOptionLabelMap as buildResolvedOptionLabelMap } from "../fields/FieldOptionHelpers";
import { normalizeClassificationGroup } from "../fields/AiFieldHelpers";
import {
  buildReviewerDisplayName,
  buildReviewerSecondaryLabel,
  buildTrustWarnings,
  getAiRecommendationLabel,
  getLinkedSubmissionId,
  getConfidenceTier,
  getOutcome,
  getOutcomeLabel,
  getPrimaryTrustWarning,
  getQualityTier,
  getReviewProgress,
  getReviewReviewerId,
  getReviewType,
  getSubmissionId,
  htmlToPlainTextPreserveBlocks,
  normalizeReviewersForAssignment,
  truncateText,
} from "./AiReviewHelpers";

const AI_REVIEW_DATA_DEBUG = true;

function logAiReviewData(event, payload) {
  if (!AI_REVIEW_DATA_DEBUG) return;
  try {
    console.log(`[buildAiReviewData] ${event}`, payload);
  } catch (error) {
    console.log(`[buildAiReviewData] ${event}`);
  }
}

function isNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "";
}

function normalizeComment(value) {
  return isNonEmptyString(value) ? value : null;
}

function normalizeSignal(signalKey, signalValue) {
  if (!signalValue || typeof signalValue !== "object") return null;
  const rating = signalValue.rating || null;
  const confidence = signalValue.confidence || null;
  if (!rating && !confidence) return null;
  return {
    key: signalKey,
    rating,
    confidence,
    raw: signalValue,
  };
}

function buildAiClassificationModel(row) {
  const classifications = row?.analysis?.structural?.classifications || null;

  return {
    speciality: normalizeClassificationGroup(classifications?.speciality),
    topic: normalizeClassificationGroup(classifications?.topic),
  };
}

function buildAiReviewModel(row) {
  const analysis = row?.analysis || null;
  const structural = analysis?.structural || null;

  if (!analysis || typeof analysis !== "object") {
    return {
      hasAnalysis: false,
      score: null,
      confidenceLevel: null,
      specialistReviewNeeded: false,
      adminComments: null,
      reviewerComments: null,
      authorComments: null,
      structureScore: null,
      titleAlignment: null,
      sectionBreakdown: null,
      vaguenessFlags: null,
      suggestions: [],
    };
  }

  return {
    hasAnalysis: true,
    score: typeof analysis.score === "number" ? analysis.score : null,
    confidenceLevel: analysis.confidenceLevel || null,
    specialistReviewNeeded: analysis.specialistReviewNeeded === true,
    adminComments: normalizeComment(analysis?.admin?.comments),
    reviewerComments: normalizeComment(analysis?.reviewer?.comments),
    authorComments: normalizeComment(analysis?.author?.comments),
    structureScore:
      typeof structural?.structureScore === "number" ? structural.structureScore : null,
    titleAlignment:
      typeof structural?.titleAlignment === "number" ? structural.titleAlignment : null,
    sectionBreakdown:
      structural?.sectionBreakdown && typeof structural.sectionBreakdown === "object"
        ? structural.sectionBreakdown
        : null,
    vaguenessFlags:
      structural?.vaguenessFlags && typeof structural.vaguenessFlags === "object"
        ? structural.vaguenessFlags
        : null,
    suggestions: Array.isArray(structural?.suggestions) ? structural.suggestions : [],
  };
}

function buildAiPresentationModel(row) {
  const presentation = row?.analysis?.presentation || null;

  if (!presentation || typeof presentation !== "object") {
    return {
      hasPresentation: false,
      combined: null,
      authorSummary: null,
      note: null,
      submissionType: null,
      signals: [],
      signalsByKey: {},
    };
  }

  const rawSignals =
    presentation.signals && typeof presentation.signals === "object"
      ? presentation.signals
      : {};

  const signalOrder = [
    "narrativeCoherence",
    "singleQuestion",
    "focusedOutcome",
    "cognitiveLoad",
  ];

  const signals = signalOrder
    .map((key) => normalizeSignal(key, rawSignals[key]))
    .filter(Boolean);

  const signalsByKey = signals.reduce((acc, signal) => {
    acc[signal.key] = signal;
    return acc;
  }, {});

  return {
    hasPresentation: true,
    combined: presentation.combined || null,
    authorSummary: normalizeComment(presentation.authorSummary),
    note: normalizeComment(presentation.note),
    submissionType: presentation.submissionType || null,
    signals,
    signalsByKey,
  };
}

function buildReasoningModel(aiReview) {
  const adminText = htmlToPlainTextPreserveBlocks(aiReview?.adminComments || "");
  const reviewerText = htmlToPlainTextPreserveBlocks(aiReview?.reviewerComments || "");
  const authorText = htmlToPlainTextPreserveBlocks(aiReview?.authorComments || "");

  const hasAdmin = !!adminText;
  const hasReviewer = !!reviewerText;
  const hasAuthor = !!authorText;

  return {
    adminText,
    adminExcerpt: truncateText(adminText, 1100),
    reviewerText,
    authorText,
    hasAdmin,
    hasReviewer,
    hasAuthor,
    outputsCount: [hasAdmin, hasReviewer, hasAuthor].filter(Boolean).length,
    notesSourceLabel: hasAdmin
      ? "Admin lead"
      : hasReviewer
        ? "Reviewer lead"
        : hasAuthor
          ? "Author lead"
          : "No outputs",
  };
}

function buildRouteDecisionModel(aiReview) {
  const score = aiReview?.score ?? null;
  const confidenceLevel = aiReview?.confidenceLevel ?? null;
  const specialistReviewNeeded = aiReview?.specialistReviewNeeded === true;

  const confTier = getConfidenceTier(confidenceLevel);
  const qualTier = getQualityTier(score);
  const outcomeKey = getOutcome(score, confidenceLevel);
  const reviewTypeLabel = getReviewType(score, confidenceLevel);
  const outcomeLabel = getOutcomeLabel(score, confidenceLevel);
  const aiRecommends = getAiRecommendationLabel(score, confidenceLevel);

  return {
    score,
    confidenceLevel,
    specialistReviewNeeded,
    confTier,
    qualTier,
    outcomeKey,
    outcomeLabel,
    reviewTypeLabel,
    aiRecommends,
    aiRouteRecommended: outcomeKey === "ai_holds",
  };
}

function buildTrustSignalsModel(aiReview) {
  const trustWarnings = buildTrustWarnings({
    specialistReviewNeeded: aiReview?.specialistReviewNeeded === true,
    sectionBreakdown: aiReview?.sectionBreakdown || null,
    structuralSuggestions: aiReview?.suggestions || [],
    structureScore: aiReview?.structureScore ?? null,
    titleAlignment: aiReview?.titleAlignment ?? null,
    vaguenessFlags: aiReview?.vaguenessFlags || null,
  });

  const primaryWarning = getPrimaryTrustWarning(trustWarnings);

  return {
    structureScore: aiReview?.structureScore ?? null,
    titleAlignment: aiReview?.titleAlignment ?? null,
    sectionBreakdown: aiReview?.sectionBreakdown || null,
    vaguenessFlags: aiReview?.vaguenessFlags || null,
    structuralSuggestions: aiReview?.suggestions || [],
    trustWarnings,
    primaryWarning,
  };
}

function buildRoutingModel({
  submissionId,
  linkedReviewItems,
  topRecommendation,
  alternativeRecommendations,
  recommendationError,
  currentSubmissionReviewCount,
  classification,
}) {
  return {
    submissionId,
    linkedReviewItems,
    topRecommendation,
    alternativeRecommendations,
    recommendationError,
    currentSubmissionReviewCount,
    classification,
    recommendedSpeciality: classification?.speciality?.recommendation || null,
    specialityMatches: classification?.speciality?.matches || [],
    recommendedTopic: classification?.topic?.recommendation || null,
    topicMatches: classification?.topic?.matches || [],
  };
}

function buildReviewerMetricsMap(reviews) {
  const metricsByReviewerId = new Map();

  (Array.isArray(reviews) ? reviews : []).forEach((review) => {
    const reviewerId = getReviewReviewerId(review);
    if (!reviewerId) return;

    const existing = metricsByReviewerId.get(reviewerId) || {
      completedCount: 0,
      scoredCount: 0,
      totalScore: 0,
    };

    const isCompleted = review?.meta?.complete === true;
    const total = Number(review?.data?.total);

    if (isCompleted) {
      existing.completedCount += 1;
    }

    if (isCompleted && Number.isFinite(total)) {
      existing.scoredCount += 1;
      existing.totalScore += total;
    }

    metricsByReviewerId.set(reviewerId, existing);
  });

  return metricsByReviewerId;
}

function attachReviewerMetrics(candidate, metricsByReviewerId) {
  if (!candidate || typeof candidate !== "object") return candidate;

  const reviewerId =
    candidate?.reviewerId != null
      ? String(candidate.reviewerId)
      : candidate?.reviewer?.id != null
        ? String(candidate.reviewer.id)
        : candidate?.reviewer?.userId != null
          ? String(candidate.reviewer.userId)
          : "";

  const metrics = reviewerId ? metricsByReviewerId.get(reviewerId) : null;
  const scoredCount = metrics?.scoredCount ?? 0;

  return {
    ...candidate,
    reviewerMetrics: {
      completedCount: metrics?.completedCount ?? 0,
      averageScore:
        scoredCount > 0 ? (metrics.totalScore ?? 0) / scoredCount : null,
    },
  };
}

function toFieldKey(value) {
  return value == null ? "" : String(value).trim().toLowerCase();
}

function findProcessedFieldConfig(viewfieldsInstance, fieldKey) {
  const wanted = toFieldKey(fieldKey);
  if (!wanted) return null;

  const processedFields =
    viewfieldsInstance?.getProcessedFields?.({ requireBuilt: true }) || [];

  return (
    processedFields.find((field) => {
      const candidates = [
        field?.key,
        field?.field,
        field?.fieldKey,
        field?.columnKey,
      ];
      return candidates.some((candidate) => toFieldKey(candidate) === wanted);
    }) || null
  );
}

function buildFieldOptionLabelMap(viewfieldsInstance, fieldConfig) {
  const resolved = viewfieldsInstance?.resolveOptionsForFieldExternal?.(fieldConfig);
  return buildResolvedOptionLabelMap({
    flatOptions: Array.isArray(resolved?.flatOptions) ? resolved.flatOptions : [],
    treeOptions: Array.isArray(resolved?.treeOptions) ? resolved.treeOptions : [],
  });
}

function mapValuesToLabels(values, labelMap) {
  return (Array.isArray(values) ? values : []).map((value) => {
    const key = value == null ? "" : String(value);
    return labelMap.get(key) || key;
  });
}

export function buildAiReviewData({
  row,
  reviews = [],
  reviewers = [],
  config = null,
  viewfieldsInstance = null,
}) {
  const submissionId = getSubmissionId(row);

  logAiReviewData("ENTERED", {
    submissionId,
    hasRow: !!row,
    reviewsCount: Array.isArray(reviews) ? reviews.length : 0,
    reviewersCount: Array.isArray(reviewers) ? reviewers.length : 0,
    hasConfig: !!config,
    hasViewfieldsInstance: !!viewfieldsInstance,
    submissionKeys: row && typeof row === "object" ? Object.keys(row).sort() : [],
  });

  let normalizedReviewers = [];

  try {
    normalizedReviewers = normalizeReviewersForAssignment(reviewers);

    logAiReviewData("normalizeReviewersForAssignment:success", {
      inputReviewerCount: Array.isArray(reviewers) ? reviewers.length : 0,
      normalizedReviewerCount: Array.isArray(normalizedReviewers) ? normalizedReviewers.length : 0,
      reviewerSamples: (Array.isArray(normalizedReviewers) ? normalizedReviewers : [])
        .slice(0, 3)
        .map((reviewer) => ({
          id: reviewer?.id ?? null,
          userId: reviewer?.userId ?? null,
          reviewer: reviewer?.reviewer === true,
          keys: reviewer && typeof reviewer === "object" ? Object.keys(reviewer).sort() : [],
          specialist_areas: reviewer?.specialist_areas,
          theme: reviewer?.theme,
          data: reviewer?.data,
        })),
    });
  } catch (error) {
    logAiReviewData("normalizeReviewersForAssignment:error", {
      message: error?.message || String(error),
      error,
    });
    throw error;
  }

  const reviewersById = new Map();

  for (const reviewer of normalizedReviewers) {
    if (reviewer?.id != null) {
      reviewersById.set(String(reviewer.id), reviewer);
    }
    if (reviewer?.userId != null) {
      reviewersById.set(String(reviewer.userId), reviewer);
    }
  }

  const linkedReviews = submissionId
    ? (Array.isArray(reviews) ? reviews : []).filter((review) => {
        return getLinkedSubmissionId(review) === submissionId;
      })
    : [];

  logAiReviewData("linkedReviews", {
    submissionId,
    linkedReviewsCount: linkedReviews.length,
    reviewIds: linkedReviews.map((review) => review?.id).filter(Boolean),
  });

  const linkedReviewItems = linkedReviews.map((review) => {
    const reviewerId = getReviewReviewerId(review);
    const reviewer = reviewersById.get(reviewerId) || null;
    const progress = getReviewProgress(review);

    return {
      review,
      reviewer,
      reviewerId,
      reviewerName: buildReviewerDisplayName(reviewer || { id: reviewerId }),
      reviewerSecondary: buildReviewerSecondaryLabel(reviewer || { id: reviewerId }),
      progress,
    };
  });

  const reviewerMetricsByReviewerId = buildReviewerMetricsMap(reviews);
  const submissionFieldConfig = findProcessedFieldConfig(
    viewfieldsInstance,
    config?.auto_assignment?.submission_field?.key
  );
  const reviewerFieldConfig = findProcessedFieldConfig(
    viewfieldsInstance,
    config?.auto_assignment?.reviewer_field?.key
  );
  const submissionOptionLabelMap = buildFieldOptionLabelMap(viewfieldsInstance, submissionFieldConfig);
  const reviewerOptionLabelMap = buildFieldOptionLabelMap(viewfieldsInstance, reviewerFieldConfig);

  let recommendationResult = null;

  logAiReviewData("recommendation:precheck", {
    submissionId,
    willCallRecommendForSubmission: !!(row && config && viewfieldsInstance),
    hasRow: !!row,
    hasConfig: !!config,
    hasViewfieldsInstance: !!viewfieldsInstance,
    normalizedReviewersCount: normalizedReviewers.length,
    reviewsCount: Array.isArray(reviews) ? reviews.length : 0,
    autoAssignmentConfig: config?.auto_assignment || null,
  });

  if (row && config && viewfieldsInstance) {
    try {
      logAiReviewData("recommendation:calling", {
        submissionId,
        normalizedReviewersCount: normalizedReviewers.length,
        reviewsCount: Array.isArray(reviews) ? reviews.length : 0,
      });

      recommendationResult = recommendForSubmission({
        submission: row,
        reviewers: normalizedReviewers,
        reviews: Array.isArray(reviews) ? reviews : [],
        config,
        viewfieldsInstance,
        getLinkedSubmissionId,
        includeInvalidCandidates: true,
      });

      recommendationResult = {
        ...recommendationResult,
        validCandidates: (recommendationResult?.validCandidates || []).map((candidate) => {
          const candidateWithMetrics = attachReviewerMetrics(candidate, reviewerMetricsByReviewerId);
          return {
            ...candidateWithMetrics,
            submissionAoELabels: mapValuesToLabels(
              candidateWithMetrics?.submissionAoE,
              submissionOptionLabelMap
            ),
            reviewerAoELabels: mapValuesToLabels(
              candidateWithMetrics?.reviewerAoE,
              reviewerOptionLabelMap
            ),
            overlapValueLabels: mapValuesToLabels(
              candidateWithMetrics?.overlapValues,
              submissionOptionLabelMap
            ),
          };
        }),
        invalidCandidates: (recommendationResult?.invalidCandidates || []).map((candidate) => {
          const candidateWithMetrics = attachReviewerMetrics(candidate, reviewerMetricsByReviewerId);
          return {
            ...candidateWithMetrics,
            submissionAoELabels: mapValuesToLabels(
              candidateWithMetrics?.submissionAoE,
              submissionOptionLabelMap
            ),
            reviewerAoELabels: mapValuesToLabels(
              candidateWithMetrics?.reviewerAoE,
              reviewerOptionLabelMap
            ),
            overlapValueLabels: mapValuesToLabels(
              candidateWithMetrics?.overlapValues,
              submissionOptionLabelMap
            ),
          };
        }),
      };

      logAiReviewData("recommendation:success", {
        submissionId,
        validCandidatesCount: recommendationResult?.validCandidates?.length || 0,
        invalidCandidatesCount: recommendationResult?.invalidCandidates?.length || 0,
        currentSubmissionReviewCount:
          recommendationResult?.currentSubmissionReviewCount ?? linkedReviews.length,
        topRecommendationReviewerId:
          recommendationResult?.validCandidates?.[0]?.reviewerId || null,
      });
    } catch (error) {
      logAiReviewData("recommendation:error", {
        submissionId,
        message: error?.message || String(error),
        error,
      });

      recommendationResult = {
        error,
        validCandidates: [],
        invalidCandidates: [],
        submission: row,
        submissionId,
        submissionAoE: [],
        currentSubmissionReviewCount: linkedReviews.length,
        limits: null,
      };
    }
  }

  const topRecommendation = recommendationResult?.validCandidates?.[0] || null;
  const alternativeRecommendations = recommendationResult?.validCandidates?.slice(1, 4) || [];
  const recommendationError = recommendationResult?.error || null;
  const currentSubmissionReviewCount =
    recommendationResult?.currentSubmissionReviewCount != null
      ? recommendationResult.currentSubmissionReviewCount
      : linkedReviews.length;

  const aiReview = buildAiReviewModel(row);
  const aiPresentation = buildAiPresentationModel(row);
  const classification = buildAiClassificationModel(row);

  const routeDecision = buildRouteDecisionModel(aiReview);
  const reasoning = buildReasoningModel(aiReview);
  const trustSignals = buildTrustSignalsModel(aiReview);
  const routing = buildRoutingModel({
    submissionId,
    linkedReviewItems,
    topRecommendation,
    alternativeRecommendations,
    recommendationError,
    currentSubmissionReviewCount,
    classification,
  });

  const result = {
    submissionId,
    normalizedReviewers,
    reviewersById,
    linkedReviews,
    linkedReviewItems,
    recommendationResult,
    topRecommendation,
    alternativeRecommendations,
    recommendationError,
    currentSubmissionReviewCount,
    aiReview,
    aiPresentation,
    classification,
    routeDecision,
    reasoning,
    trustSignals,
    routing,
  };

  logAiReviewData("RETURN", {
    submissionId,
    hasRecommendationResult: !!recommendationResult,
    hasRecommendationError: !!recommendationError,
    topRecommendationReviewerId: topRecommendation?.reviewerId || null,
    alternativesCount: alternativeRecommendations.length,
    linkedReviewItemsCount: linkedReviewItems.length,
  });

  return result;
}
